import { select } from 'd3-selection';
import * as d3 from 'd3';
import config from './config';

// styles
import './styles/graphic.scss';

const DATA_URL = 'https://ix.cnn.io/data/drug-boats/drug_boat_strikes_bot.json';

let cachedData = null;
let dataPromise = null;

// Render small-multiple charts side by side on desktop and stacked on mobile.
export default function renderMultiples(props, container, isMobile) {
  // Use cached data if available to avoid re-fetching on resize
  const loadData = cachedData
    ? Promise.resolve(cachedData)
    : (dataPromise || (dataPromise = d3.json(DATA_URL).then((data) => {
      cachedData = data;
      return data;
    })));

  loadData
    .then((data) => {
      const { metadata } = data || {};
      const timeSeries = (metadata && metadata.time_series) || [];

      // Calculate shared Y-axis domain across both metrics
      const parser = d3.timeParse('%Y-%m-%d');
      const attacksValues = timeSeries.map(d => ({
        date: parser(d.date),
        value: d.cumulative_attacks != null ? +d.cumulative_attacks : null,
      })).filter(d => d.date && d.value != null);

      const deathsValues = timeSeries.map(d => ({
        date: parser(d.date),
        value: d.cumulative_deaths != null ? +d.cumulative_deaths : null,
      })).filter(d => d.date && d.value != null);

      const maxAttacks = d3.max(attacksValues, d => d.value) || 0;
      const maxDeaths = d3.max(deathsValues, d => d.value) || 0;
      const sharedMaxY = Math.max(maxAttacks, maxDeaths);

      const attacksConfig = config(props, timeSeries, {
        valueField: 'cumulative_attacks',
        label: 'Attacks',
        color: '#53a796',
        sharedMaxY,
      });

      const deathsConfig = config(props, timeSeries, {
        valueField: 'cumulative_deaths',
        label: 'Deaths',
        color: '#f18851',
        sharedMaxY,
      });

      const charts = [attacksConfig, deathsConfig];

      const wrapper = container.append('div')
        .attr('class', 'charts-wrapper');

      const chartContainers = wrapper.selectAll('.chart')
        .data(charts)
        .enter()
        .append('div')
        .attr('class', 'chart');

      chartContainers.each(function renderChartInstance(chartProps) {
        const chartContainer = select(this);

        if (chartProps.title) {
          chartContainer.append('div')
            .attr('class', 'chart-title')
            .text(chartProps.title);
        }

        renderSingle(chartProps, chartContainer, isMobile);
      });

      // Build dek from metadata (only if not already set)
      if (metadata && metadata.total_attacks && metadata.total_deaths && timeSeries.length > 0) {
        const dekNode = document.querySelector('.subhed.dek');
        if (dekNode && !dekNode.classList.contains('updated')) {
          const parser = d3.timeParse('%Y-%m-%d');
          const firstDate = parser(timeSeries[0].date);
          const firstDateFormatted = firstDate.toLocaleDateString('en-US', {
            month: 'long',
            year: 'numeric',
          });

          const dekText = `There have been at least <strong>${metadata.total_attacks}</strong> <span class="key-strikes">attacks</span> on boats in the Caribbean Sea and Pacific Ocean since ${firstDateFormatted}, resulting in <strong>${metadata.total_deaths}</strong> reported <span class="key-deaths">deaths</span>.`;

          dekNode.innerHTML = dekText;
          dekNode.classList.add('updated');
        }
      }

      // Update footnote with chart date and last attack date (only if not already set)
      if (metadata && metadata.lastAttackStr) {
        const footnoteNode = document.querySelector('.footnote');
        if (footnoteNode && !footnoteNode.classList.contains('updated')) {
          const yesterday = new Date();
          yesterday.setDate(yesterday.getDate() - 1);

          // Format date as "Nov. 30, 2025" to match lastAttackStr format
          let month = yesterday.toLocaleDateString('en-US', { month: 'short' });
          // Add period if not already present
          if (!month.endsWith('.')) {
            month += '.';
          }
          const day = yesterday.getDate();
          const year = yesterday.getFullYear();
          const chartDate = `${month} ${day}, ${year}`;

          const existingText = footnoteNode.innerHTML;
          const updatedText = existingText
            ? `${existingText} Note: Cumulative chart values current as of ${chartDate}. Date of last known attack: ${metadata.lastAttackStr}.`
            : `Note: Cumulative chart values current as of ${chartDate}. Date of last known attack: ${metadata.lastAttackStr}.`;
          footnoteNode.innerHTML = updatedText;
          footnoteNode.classList.add('updated');
        }
      }

      const { pymChild } = window;
      if (pymChild) {
        pymChild.sendHeight();
      }
    })
    .catch(error => console.error('Error fetching data:', error));
}

function renderSingle(props, container, isMobile) {
  const {
    color,
    data,
    fmtYearAbbrev,
    fmtYearFull,
    fmtMonth,
    height,
    initialWidth,
    initialHeight,
    margins,
    path,
    areaPath,
    width,
    x,
    xAxis,
    y,
    yAxis,
  } = props;

  const xTicks = isMobile ? 3 : 4;
  const yTicks = isMobile ? 4 : 5;

  const xGrid = xAxis(x)
    .ticks(xTicks)
    .tickFormat(d3.timeFormat(isMobile ? '%b. %Y' : '%b. %Y'));

  // svg
  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', initialHeight);

  const g = svg.append('g')
    .attr('transform', `translate(${[margins.left, margins.top]})`);

  // x axis
  g.append('g')
    .attr('class', 'grid x')
    .attr('transform', `translate(${[0, height]})`)
    .call(xGrid);

  // y axis
  const yGrid = g.append('g')
    .attr('class', 'grid y')
    .call(yAxis(y).ticks(yTicks));

  // keep ticks as whole numbers for counts
  yGrid.selectAll('text')
    .text(d => `${d}`);

  yGrid.selectAll('line').attr('x2', width);

  // lines
  const lines = g.append('g')
    .attr('class', 'lines')
    .selectAll('g')
    .data(data)
    .enter()
    .append('g')
    .attr('class', 'line');

  // area fills
  lines.append('path')
    .attr('class', 'area-fill')
    .attr('fill', d => color(d.id))
    .attr('stroke', 'none')
    .attr('opacity', 0.2)
    .attr('d', d => areaPath(d.values));

  // line strokes
  lines.append('path')
    .attr('stroke', d => color(d.id))
    .attr('d', d => path(d.values));

  // endpoint circles
  lines.append('circle')
    .attr('cx', (d) => {
      const hasValues = d && d.values && d.values.length;
      if (!hasValues) return 0;
      const d0 = d.values[d.values.length - 1];
      return x(d0.date);
    })
    .attr('cy', (d) => {
      const hasValues = d && d.values && d.values.length;
      if (!hasValues) return 0;
      const d0 = d.values[d.values.length - 1];
      return y(d0.value);
    })
    .attr('r', 4)
    .attr('fill', d => color(d.id))
    .attr('stroke', '#fff')
    .attr('stroke-width', 1.5);

  // labels
  const label = lines.append('g')
    .attr('class', 'data-label');

  label.append('text')
    .attr('transform', (d) => {
      const hasValues = d && d.values && d.values.length;
      if (!hasValues) {
        return null;
      }
      const d0 = d.values[d.values.length - 1];
      const x0 = x(d0.date) + 8;
      const y0 = y(d0.value) + 4;
      return `translate(${[x0, y0]})`;
    })
    .text((d) => {
      const hasValues = d && d.values && d.values.length;
      if (!hasValues) {
        return '';
      }
      const d0 = d.values[d.values.length - 1];
      const { value } = d0;
      const rounded = Math.round(value);
      return `${rounded}`;
    });
}
